import { useEffect } from 'react';
import Intercom from '@intercom/messenger-js-sdk';

import { User } from '../../pages/settings/users/useUser';

/**
 * Lanceur Intercom du back-office. Le serveur décide (`intercom_enabled`) : faux pour l'équipe
 * TriggerFlow, quand la bulle TriggerFlow remplace Intercom, ou quand Intercom est coupé dans
 * Support > Réglages. Code gardé tant que le SDK n'est pas retiré (livraison suivante).
 */
export default function useIntercomLauncher(token: string | null | undefined, user: User | null | undefined, pathname: string) {
    useEffect(() => {
        if (!token || !user?.id) return;

        if (!user.intercom_enabled) {
            if (window.Intercom) window.Intercom('shutdown');
            return;
        }

        // created_at attendu par Intercom = timestamp Unix en secondes (l'API renvoie "Y-m-d H:i:s")
        const createdAtUnix = user.created_at
            ? Math.floor(new Date(user.created_at.replace(' ', 'T')).getTime() / 1000)
            : undefined;

        // Les attributs custom et la société sont portés par le JWT signé côté backend
        // (intercom_user_jwt) : c'est la seule voie « vérifiée » qu'Intercom accepte
        // pour écrire ces données. Nom/email restent ici pour l'affichage immédiat.
        Intercom({
            app_id: 'lm7qjndk',
            user_id: user.id.toString(),
            name: [user.firstname, user.lastname].filter(Boolean).join(' ') || undefined,
            email: user.email,
            created_at: createdAtUnix,
            language_override: user.locale,
            intercom_user_jwt: user.intercom_user_jwt,
            // Repli si le JWT n'est pas encore disponible (auth de secours).
            user_hash: user.intercom_user_jwt ? undefined : user.user_hash,
        });
    }, [token, user?.id, user?.intercom_enabled]);

    // Cacher le launcher Intercom sur les pages messagerie & cardex (il gêne les boutons en bas à droite)
    useEffect(() => {
        if (!user?.intercom_enabled) return; // Intercom non démarré (voir plus haut).

        const isOverlayPage = pathname.startsWith('/messaging') || pathname.startsWith('/cardex');
        if (window.Intercom) {
            window.Intercom('update', { hide_default_launcher: isOverlayPage });
        }
    }, [pathname, user?.intercom_enabled]);
}
